import React, { useState, useRef } from 'react';
import { PhotoIcon, TrashIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
// ✅ PHASE 4: Import getApiBaseUrl for dynamic API endpoint
import { getApiBaseUrl } from '../../config/region';

const ImageUpload = ({ currentImage, onImageUpdate, disabled = false }) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      uploadImage(file);
    }
  };

  const uploadImage = async (file) => {
    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('vd.imgUploadInvalidType'));
      setIsUploading(false);
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('vd.imgUploadTooLarge'));
      setIsUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append('profileImage', file);

    try {
      const token = localStorage.getItem('venue_app_token');
      if (!token) {
        throw new Error(t('vd.imgUploadNotAuthenticated'));
      }

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) return prev + 10;
          return prev;
        });
      }, 100);

      // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
      const response = await fetch(`${getApiBaseUrl()}/upload/profile-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-region': 'th', // ✅ PHASE 4: Fixed region from india to th
        },
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (response.ok) {
        onImageUpdate(data.imageUrl);
        setTimeout(() => {
          setUploadProgress(0);
          setIsUploading(false);
        }, 500);
      } else {
        throw new Error(data.message || t('vd.imgUploadFailed'));
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || t('vd.imgUploadFailedToUpload'));
      setUploadProgress(0);
      setIsUploading(false);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!currentImage) return;

    setIsUploading(true);
    setError(null);

    try {
      const token = localStorage.getItem('venue_app_token');
      if (!token) {
        throw new Error(t('vd.imgUploadNotAuthenticated'));
      }

      // ✅ PHASE 4: Use getApiBaseUrl() instead of hardcoded domain
      const response = await fetch(`${getApiBaseUrl()}/upload/profile-image`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-region': 'th', // ✅ PHASE 4: Fixed region from india to th
        },
      });

      const data = await response.json();

      if (response.ok) {
        onImageUpdate('');
      } else {
        throw new Error(data.message || t('vd.imgUploadDeleteFailed'));
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || t('vd.imgUploadFailedToDelete'));
    }

    setIsUploading(false);
  };

  const triggerFileSelect = () => {
    if (!disabled && !isUploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{ 
        fontSize: 14, 
        fontWeight: 600, 
        color: disabled ? '#9ca3af' : '#374151',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}>
        <PhotoIcon style={{ height: 16, width: 16 }} />
        {t('vd.imgUploadProfilePicture')}
      </label>

      <div style={{
        position: 'relative',
        border: '2px dashed #d1d5db',
        borderRadius: 12,
        padding: 20,
        textAlign: 'center',
        backgroundColor: disabled ? '#f9fafb' : 'white',
        cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease',
        minHeight: 120
      }}
      onClick={triggerFileSelect}
      onMouseEnter={e => {
        if (!disabled && !isUploading) {
          e.currentTarget.style.borderColor = '#ea580c';
          e.currentTarget.style.backgroundColor = '#fef7f0';
        }
      }}
      onMouseLeave={e => {
        if (!disabled && !isUploading) {
          e.currentTarget.style.borderColor = '#d1d5db';
          e.currentTarget.style.backgroundColor = 'white';
        }
      }}>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled || isUploading}
        />

        <AnimatePresence mode="wait">
          {isUploading ? (
            <motion.div
              key="uploading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12
              }}
            >
              <ArrowUpTrayIcon style={{ height: 32, width: 32, color: '#ea580c' }} />
              <div style={{ width: '100%', maxWidth: 200 }}>
                <div style={{
                  width: '100%',
                  height: 4,
                  backgroundColor: '#e5e7eb',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    backgroundColor: '#ea580c',
                    borderRadius: 2,
                    width: `${uploadProgress}%`,
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <p style={{ 
                  margin: '8px 0 0 0', 
                  fontSize: 12, 
                  color: '#6b7280' 
                }}>
                  {t('vd.imgUploadUploadingPercent', { pct: uploadProgress })}
                </p>
              </div>
            </motion.div>
          ) : currentImage ? (
            <motion.div
              key="image"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12
              }}
            >
              <img 
                src={currentImage}
                alt={t('vd.imgUploadProfilePicture')}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid #ea580c'
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerFileSelect();
                  }}
                  disabled={disabled}
                  style={{
                    padding: '6px 12px',
                    fontSize: 12,
                    backgroundColor: '#ea580c',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1
                  }}
                >
                  {t('vd.imgUploadChange')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  disabled={disabled}
                  style={{
                    padding: '6px 12px',
                    fontSize: 12,
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <TrashIcon style={{ height: 12, width: 12 }} />
                  {t('vd.imgUploadRemove')}
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8
              }}
            >
              <PhotoIcon style={{ 
                height: 32, 
                width: 32, 
                color: disabled ? '#9ca3af' : '#6b7280' 
              }} />
              <p style={{ 
                margin: 0, 
                fontSize: 14, 
                color: disabled ? '#9ca3af' : '#6b7280' 
              }}>
                {disabled ? t('vd.imgUploadDisabled') : t('vd.imgUploadClickToUpload')}
              </p>
              <p style={{
                margin: 0,
                fontSize: 12,
                color: disabled ? '#9ca3af' : '#9ca3af'
              }}>
                {t('vd.imgUploadFormatsHint')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            padding: 8,
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            borderRadius: 6,
            fontSize: 12,
            textAlign: 'center',
            border: '1px solid #fecaca'
          }}
        >
          {error}
        </motion.div>
      )}
    </div>
  );
};

export default ImageUpload;
